import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionContext';
import { useCanAccessInvestmentProposals } from '../hooks/useCanAccessInvestmentProposals';
import { useCanAccessCapitalInvestment } from '../hooks/useCanAccessCapitalInvestment';
import {
  ADMIN_NAV_CARDS,
  AdminNavCard,
  AdminNavPrefs,
  cardForPath,
  isRarelyUsed,
  mergeAdminNavPrefs,
  mostUsedCards,
  recordOpen,
  usesAdminDashboard,
  visibleAdminCards,
} from '../config/adminNavCards';

/**
 * Admin navigation state shared by the dashboard sections and the left
 * sidebar: which cards this user may see, which they use, which are pinned,
 * and which have gone unused long enough to tuck into "Rarely Used".
 *
 * Usage is recorded on ROUTE CHANGE (cards, sidebar, Cmd+K, deep links and
 * back-button all count), throttled to one write per card per hour, and
 * stored on the user's own doc at `preferences.adminNav` (a field users may
 * already update themselves — no rules change). Mirrored to localStorage for
 * instant paint. Never written while impersonating.
 */

interface AdminNavContextValue {
  /** True for roles on the HQ/admin dashboard — false hides the sidebar entirely. */
  enabled: boolean;
  /** Every card this user may open (permissions + beta gates). */
  cards: AdminNavCard[];
  /** Cards still in their sections (not rarely used). */
  activeCards: AdminNavCard[];
  rarelyUsed: AdminNavCard[];
  pinned: AdminNavCard[];
  mostUsed: AdminNavCard[];
  isPinned: (id: string) => boolean;
  togglePin: (id: string) => void;
  lastOpened: (id: string) => number | null;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
}

const AdminNavContext = createContext<AdminNavContextValue | null>(null);

const storageKey = (userId: string) => `adminNav:${userId}`;

function readLocal(userId: string): AdminNavPrefs | undefined {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? (JSON.parse(raw) as AdminNavPrefs) : undefined;
  } catch {
    return undefined;
  }
}

function writeLocal(userId: string, prefs: AdminNavPrefs): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    // Storage blocked (private mode) — Firestore still holds the prefs.
  }
}

export const AdminNavProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userProfile, isImpersonating } = useAuth();
  const { hasPermission, hasHierarchicalPermissions } = usePermissions();
  const { canAccess: canAccessProposals } = useCanAccessInvestmentProposals();
  const { canAccess: canAccessCapital } = useCanAccessCapitalInvestment();
  const location = useLocation();

  const userId = userProfile?.id ?? '';
  const enabled = usesAdminDashboard(userProfile?.role);

  const [prefs, setPrefs] = useState<AdminNavPrefs>({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Set by update(); the save effect below persists only user-driven changes, not the initial load. */
  const dirty = useRef(false);

  // Load: local copy first (instant), merged with the user doc's copy.
  useEffect(() => {
    if (!userId) return;
    const server = (userProfile as { preferences?: { adminNav?: AdminNavPrefs } } | null)?.preferences?.adminNav;
    setPrefs(mergeAdminNavPrefs(readLocal(userId), server));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const persist = useCallback(
    (next: AdminNavPrefs) => {
      if (!userId) return;
      writeLocal(userId, next);
      if (isImpersonating) return; // never write into someone else's doc
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        // JSON round-trip drops undefined fields (Firestore rejects them).
        const clean = JSON.parse(JSON.stringify(next)) as AdminNavPrefs;
        setDoc(doc(db, 'users', userId), { preferences: { adminNav: clean } }, { merge: true }).catch((err) =>
          console.warn('[AdminNav] preference save failed', err),
        );
      }, 2000);
    },
    [userId, isImpersonating],
  );

  const update = useCallback((fn: (p: AdminNavPrefs) => AdminNavPrefs | null) => {
    setPrefs((current) => {
      const next = fn(current);
      if (!next) return current;
      dirty.current = true;
      return next;
    });
  }, []);

  // Persist outside the state updater (updaters must stay pure).
  useEffect(() => {
    if (!dirty.current) return;
    dirty.current = false;
    persist(prefs);
  }, [prefs, persist]);

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const cards = useMemo(
    () =>
      enabled
        ? visibleAdminCards(ADMIN_NAV_CARDS, {
            role: userProfile?.role,
            hasHierarchicalPermissions,
            hasPermission,
            canAccessProposals,
            canAccessCapital,
          })
        : [],
    [enabled, userProfile?.role, hasHierarchicalPermissions, hasPermission, canAccessProposals, canAccessCapital],
  );

  // Record a visit whenever the route lands on a card's page.
  useEffect(() => {
    if (!enabled || !userId || isImpersonating) return;
    const card = cardForPath(location.pathname, cards);
    if (!card) return;
    update((p) => recordOpen(p, card.id, Date.now()));
  }, [location.pathname, enabled, userId, isImpersonating, cards, update]);

  // Close the mobile drawer on navigation.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const value = useMemo<AdminNavContextValue>(() => {
    const now = Date.now();
    const rarely = cards.filter((c) => isRarelyUsed(c.id, prefs, now));
    const rarelyIds = new Set(rarely.map((c) => c.id));
    const pinnedIds = prefs.pinned ?? [];
    const activeCards = cards.filter((c) => !rarelyIds.has(c.id));
    return {
      enabled,
      cards,
      activeCards,
      rarelyUsed: rarely,
      pinned: pinnedIds.map((id) => cards.find((c) => c.id === id)).filter((c): c is AdminNavCard => !!c),
      mostUsed: mostUsedCards(activeCards, prefs, now, 8),
      isPinned: (id) => pinnedIds.includes(id),
      togglePin: (id) =>
        update((p) => {
          const list = p.pinned ?? [];
          return {
            ...p,
            pinned: list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
            updatedAt: Date.now(),
          };
        }),
      lastOpened: (id) => prefs.usage?.[id]?.last ?? null,
      sidebarCollapsed: !!prefs.sidebarCollapsed,
      setSidebarCollapsed: (v) => update((p) => ({ ...p, sidebarCollapsed: v, updatedAt: Date.now() })),
      mobileOpen,
      setMobileOpen,
      paletteOpen,
      setPaletteOpen,
    };
  }, [enabled, cards, prefs, update, mobileOpen, paletteOpen]);

  return <AdminNavContext.Provider value={value}>{children}</AdminNavContext.Provider>;
};

export function useAdminNav(): AdminNavContextValue {
  const ctx = useContext(AdminNavContext);
  if (!ctx) throw new Error('useAdminNav must be used inside AdminNavProvider');
  return ctx;
}
